import React, { useState } from "react";
import {
  useAddScrapbookCommentMutation,
  useDeleteScrapbookCommentMutation,
} from "../../../store/slices/api";
import { formatDate, shortName } from "./util";

// Kept in step with ScrapbookComment.MAX_LENGTH on the server, which enforces it
// for real. This number only decides when the counter turns amber.
const MAX_LENGTH = 244;

// Sam, 2026-09-16: "i want the comment and reply sections to start hidding
// commetns dispalyed after 7 comments". The NEWEST seven stay on screen; the
// older ones fold away behind one line you can open. Nothing is deleted, and a
// thread never pushes the next photograph off the page.
const SHOWN_AT_FIRST = 7;

function Comments({ post, viewerId, isAdmin }) {
  const [body, setBody] = useState("");
  const [addComment, { isLoading }] = useAddScrapbookCommentMutation();
  const [deleteComment] = useDeleteScrapbookCommentMutation();
  const [error, setError] = useState("");

  const comments = post.comments || [];
  const [showAll, setShowAll] = useState(false);
  const folded = !showAll && comments.length > SHOWN_AT_FIRST;
  const onScreen = folded ? comments.slice(comments.length - SHOWN_AT_FIRST) : comments;

  const submit = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setError("");
    try {
      await addComment({ postId: post.id, body: text }).unwrap();
      setBody("");
    } catch (err) {
      setError(err?.data?.error || "That didn’t send. Try again in a moment.");
    }
  };

  // Your own comment, or one on your own photo, or you're an admin. Mirrors the
  // rule the server enforces — the server is what actually decides.
  const canRemove = (c) =>
    c.author_individual_id === viewerId ||
    post.author_individual_id === viewerId ||
    isAdmin;

  const remaining = MAX_LENGTH - body.length;

  return (
    <div className="px-[16px] pb-[14px]">
      {/* Each comment is its OWN floating cell — no connecting rail, no
          stubs, nothing implying a thread, because it isn't one. Each lifts
          independently on hover, less than the post above it so the hierarchy
          holds. */}
      {comments.length > 0 && (
        <ul className="list-none p-0 m-0 mb-[11px] flex flex-col gap-[10px]">
          {folded && (
            <li>
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="w-full px-[14px] py-[8px] rounded-[12px]
                           border border-dashed border-dupontGoldLt/60
                           font-blairBody text-[12px] font-semibold tracking-[0.04em]
                           text-dupontGoldDk hover:bg-dupontCream/70 transition-colors
                           motion-reduce:transition-none"
              >
                Show all {comments.length} comments
              </button>
            </li>
          )}
          {onScreen.map((c) => (
            <li
              key={c.id}
              className="group relative bg-dupontCream/60 dark:bg-neutral-800/60
                         border border-dupontGoldLt/40 rounded-[12px] px-[14px] py-[10px]
                         shadow-sm transition-[transform,box-shadow,background] duration-200 ease-out
                         hover:-translate-y-[2px] hover:bg-white dark:hover:bg-neutral-800
                         hover:shadow-md
                         motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <span className="block font-display font-semibold text-[13px] leading-[1.3] tracking-[0.01em]
                               text-dupontBlue dark:text-darkText mb-[3px]">
                {shortName(c.author)}
              </span>
              <span className="font-body text-[16.5px] leading-[1.5] text-gray-800 dark:text-gray-300 break-words">
                {c.body}
              </span>
              {canRemove(c) && (
                <button
                  type="button"
                  onClick={() => deleteComment({ commentId: c.id, postId: post.id })}
                  aria-label={`Remove ${c.author || "this"} comment`}
                  title={formatDate(c.created_at)}
                  className="absolute top-[8px] right-[11px]
                             opacity-0 group-hover:opacity-100 focus:opacity-100
                             text-[12px] text-gray-400 hover:text-red-600 transition-opacity
                             [@media(hover:none)]:opacity-100"
                >
                  remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex items-start gap-[8px]">
        <input
          type="text"
          value={body}
          maxLength={MAX_LENGTH}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Say something…"
          aria-label="Add a comment"
          className="flex-1 min-w-0 px-[13px] py-[9px] rounded-[10px] font-body text-[16px]
                     bg-white dark:bg-neutral-800 border border-dupontGoldLt/40
                     text-dupontBlue dark:text-darkText
                     focus:outline-none focus:ring-2 focus:ring-dupontBlue dark:focus:ring-dupontGoldLt"
        />
        <button
          type="submit"
          disabled={isLoading || !body.trim()}
          className="px-[16px] py-[9px] rounded-[10px] font-blairBody text-[12.5px] font-semibold tracking-[0.04em]
                     bg-dupontBlue text-white disabled:opacity-40
                     dark:bg-dupontGoldLt dark:text-dupontBlue"
        >
          Post
        </button>
      </form>

      {/* Only appears when it starts to matter, so it isn't nagging at you from
          the first keystroke. */}
      {body.length > MAX_LENGTH - 60 && (
        <p className={`mt-[5px] text-[12px] ${remaining <= 20 ? "text-amber-700" : "text-gray-400"}`}>
          {remaining} characters left
        </p>
      )}
      {error && <p className="mt-[5px] text-[13px] text-red-600">{error}</p>}
    </div>
  );
}

export default Comments;
